import { useState, useRef, useEffect } from 'react'
import { FOOD_LIST, FOODS, computeItemNutrition, computeMealTotals } from '../data/foods'
import { useMealTemplates } from '../lib/hooks'
import { fileToCompressedDataUrl } from '../lib/image'

// Searchable food picker - a plain <select> has no way to filter its ~30
// options by typing, so this renders a text input with a custom dropdown
// list instead (a native <datalist> would dodge that, but its popup ignores
// dark-theme CSS in every major browser).
function FoodSelect({ value, onChange }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const wrapRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filtered = query
    ? FOOD_LIST.filter((f) => f.name.toLowerCase().includes(query.toLowerCase()))
    : FOOD_LIST

  const pick = (key) => {
    onChange(key)
    setQuery('')
    setOpen(false)
  }

  return (
    <div className="food-select" ref={wrapRef}>
      <input
        type="text"
        className="food-select-input"
        placeholder="Search food..."
        value={open ? query : FOODS[value]?.name || ''}
        onFocus={() => {
          setQuery('')
          setOpen(true)
          setHighlight(0)
        }}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
          setHighlight(0)
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlight((h) => Math.min(h + 1, filtered.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlight((h) => Math.max(h - 1, 0))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            if (filtered[highlight]) pick(filtered[highlight].key)
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
      />
      {open && (
        <div className="food-select-list">
          {filtered.length === 0 && <div className="food-select-empty">No matches</div>}
          {filtered.map((f, i) => (
            <div
              key={f.key}
              className={`food-select-option ${i === highlight ? 'is-highlighted' : ''} ${f.key === value ? 'is-selected' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(f.key)}
              onMouseEnter={() => setHighlight(i)}
            >
              {f.name}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Dropdown of saved meal templates - applying one replaces the current item
// list wholesale, like starting the meal fresh from that template.
function TemplateBar({ templates, onApply, onDelete }) {
  const [open, setOpen] = useState(false)
  const list = Object.entries(templates)
  if (list.length === 0) return null
  return (
    <div className="template-bar">
      <button className="template-toggle" onClick={() => setOpen((o) => !o)} type="button">
        FROM TEMPLATE {open ? '▴' : '▾'}
      </button>
      {open && (
        <div className="template-list">
          {list.map(([id, t]) => (
            <div key={id} className="template-row">
              <button className="template-name" onClick={() => { onApply(t.items); setOpen(false) }} type="button">{t.name}</button>
              <button className="template-delete" onClick={() => onDelete(id)} type="button" aria-label="Delete template">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function MealEditor({ items, isCustom, onSave, onReset, onClose }) {
  const [localItems, setLocalItems] = useState(items)
  const [addFoodKey, setAddFoodKey] = useState(FOOD_LIST[0]?.key || '')
  const [addQty, setAddQty] = useState('')
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const { templates, saveTemplate, deleteTemplate } = useMealTemplates()
  const [scanBusy, setScanBusy] = useState(false)
  const [scanError, setScanError] = useState('')
  const [scanUnmatched, setScanUnmatched] = useState([])
  const [showRecipe, setShowRecipe] = useState(false)

  const totals = computeMealTotals(localItems)
  const addQtyNum = parseFloat(addQty)
  const previewNutrition = Number.isFinite(addQtyNum) && addQtyNum > 0 ? computeItemNutrition(addFoodKey, addQtyNum) : null
  const selectedFood = FOODS[addFoodKey]
  const selectedRecipe = selectedFood?.recipe
  const hasRecipeItems = selectedRecipe?.items?.length > 0

  const addRecipeIngredients = () => {
    if (!hasRecipeItems) return
    setLocalItems([...localItems, ...selectedRecipe.items.map((it) => ({ ...it }))])
    setShowRecipe(false)
  }

  const removeItem = (index) => setLocalItems(localItems.filter((_, i) => i !== index))
  const updateQty = (index, qty) => {
    const val = parseFloat(qty)
    setLocalItems(localItems.map((it, i) => (i === index ? { ...it, qty: Number.isFinite(val) ? val : 0 } : it)))
  }
  const addItem = () => {
    const qty = parseFloat(addQty)
    if (!addFoodKey || !Number.isFinite(qty) || qty <= 0) return
    setLocalItems([...localItems, { foodKey: addFoodKey, qty }])
    setAddQty('')
  }
  const confirmSaveTemplate = () => {
    if (!templateName.trim() || localItems.length === 0) return
    saveTemplate(templateName.trim(), localItems)
    setTemplateName('')
    setSavingTemplate(false)
  }

  const handleScanPhoto = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setScanBusy(true)
    setScanError('')
    setScanUnmatched([])
    try {
      const dataUrl = await fileToCompressedDataUrl(file)
      const imageBase64 = dataUrl.split(',')[1]
      const res = await fetch('/api/analyze-meal', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          imageBase64,
          mimeType: 'image/jpeg',
          foodList: FOOD_LIST.map((f) => ({ key: f.key, name: f.name, unit: f.unit })),
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || 'AI analysis failed')
      }
      const { items } = await res.json()
      const matched = []
      const unmatched = []
      for (const item of items || []) {
        if (item.foodKey && FOODS[item.foodKey] && Number.isFinite(item.qty) && item.qty > 0) {
          matched.push({ foodKey: item.foodKey, qty: item.qty })
        } else if (item.name) {
          unmatched.push(item.name)
        }
      }
      if (matched.length > 0) setLocalItems((prev) => [...prev, ...matched])
      setScanUnmatched(unmatched)
      if (matched.length === 0 && unmatched.length === 0) {
        setScanError("Couldn't identify any food in that photo - try a clearer shot.")
      }
    } catch (err) {
      setScanError(err.message || 'Something went wrong analyzing the photo.')
    } finally {
      setScanBusy(false)
    }
  }

  return (
    <div className="meal-editor">
      <TemplateBar templates={templates} onApply={setLocalItems} onDelete={deleteTemplate} />

      <label className="btn btn-ghost full-width scan-photo-btn">
        {scanBusy ? 'ANALYZING PHOTO…' : '📷 SCAN PHOTO (AI)'}
        <input type="file" accept="image/*" capture="environment" onChange={handleScanPhoto} disabled={scanBusy} />
      </label>
      {scanError && <p className="error-text" style={{ marginTop: 8 }}>{scanError}</p>}
      {scanUnmatched.length > 0 && (
        <p className="card-subtitle" style={{ marginTop: 8 }}>
          Added what it recognized. Not in the database, add manually: {scanUnmatched.join(', ')}
        </p>
      )}

      {localItems.length === 0 && <p className="card-subtitle">No items yet - add something below.</p>}
      {localItems.map((item, i) => {
        const food = FOODS[item.foodKey]
        const n = computeItemNutrition(item.foodKey, item.qty)
        return (
          <div key={i} className="editor-item-row">
            <div className="editor-item-top">
              <span className="editor-item-name">{food?.name || item.foodKey}</span>
              <input
                type="number"
                inputMode="decimal"
                className="editor-item-qty"
                value={item.qty}
                onChange={(e) => updateQty(i, e.target.value)}
              />
              <span className="editor-item-unit">{food?.unit}</span>
              <button className="editor-item-remove" onClick={() => removeItem(i)} type="button" aria-label="Remove">×</button>
            </div>
            <div className="editor-item-macros">
              <span>{n.kcal} kcal</span>
              <span>{n.protein}g protein</span>
              <span>{n.carbs}g carbs</span>
              <span>{n.fat}g fat</span>
            </div>
            <div className="editor-item-detail">
              <span>{n.fiber}g fiber</span>
              <span>{n.calcium}mg calcium</span>
              <span>{n.iron}mg iron</span>
              <span>{n.zinc}mg zinc</span>
              <span>{n.potassium}mg potassium</span>
              <span>{n.sodium}mg sodium</span>
            </div>
          </div>
        )
      })}

      <div className="editor-add-row">
        <FoodSelect
          value={addFoodKey}
          onChange={(key) => {
            setAddFoodKey(key)
            setShowRecipe(false)
          }}
        />
        <input
          type="number"
          inputMode="decimal"
          className="editor-add-qty"
          placeholder={FOODS[addFoodKey]?.unit === 'pcs' ? 'qty' : FOODS[addFoodKey]?.unit}
          value={addQty}
          onChange={(e) => setAddQty(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addItem()}
        />
        <button className="editor-add-btn" onClick={addItem} type="button">+ ADD</button>
      </div>

      {selectedRecipe && (
        <div className="recipe-bar">
          <div className="recipe-bar-actions">
            <button className="template-toggle" onClick={() => setShowRecipe((v) => !v)} type="button">
              {showRecipe ? 'HIDE RECIPE ▴' : 'VIEW RECIPE ▾'}
            </button>
            {hasRecipeItems && (
              <button className="template-toggle" onClick={addRecipeIngredients} type="button">
                BREAK INTO INGREDIENTS
              </button>
            )}
          </div>
          {showRecipe && (
            <div className="recipe-detail">
              <p className="recipe-section-label">INGREDIENTS</p>
              <ul className="recipe-list">
                {selectedRecipe.ingredients.map((ing, i) => <li key={i}>{ing}</li>)}
              </ul>
              <p className="recipe-section-label">STEPS</p>
              <ol className="recipe-list recipe-steps">
                {selectedRecipe.steps.map((step, i) => <li key={i}>{step}</li>)}
              </ol>
              <p className="recipe-tip"><strong>Health tip:</strong> {selectedRecipe.healthTip}</p>
              <p className="recipe-tip"><strong>Lighter swap:</strong> {selectedRecipe.healthierSwap}</p>
              {hasRecipeItems && (
                <p className="card-subtitle" style={{ marginTop: 8 }}>
                  Tap "Break into ingredients" above to add {selectedRecipe.items.length} editable ingredients instead of logging this as one item - adjust any quantity and the nutrition recalculates automatically.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="editor-preview">
        {previewNutrition ? (
          <>
            <div className="editor-item-macros">
              <span>{previewNutrition.kcal} kcal</span>
              <span>{previewNutrition.protein}g protein</span>
              <span>{previewNutrition.carbs}g carbs</span>
              <span>{previewNutrition.fat}g fat</span>
            </div>
            <div className="editor-item-detail">
              <span>{previewNutrition.fiber}g fiber</span>
              <span>{previewNutrition.calcium}mg calcium</span>
              <span>{previewNutrition.iron}mg iron</span>
              <span>{previewNutrition.zinc}mg zinc</span>
              <span>{previewNutrition.potassium}mg potassium</span>
              <span>{previewNutrition.sodium}mg sodium</span>
            </div>
          </>
        ) : (
          <p className="editor-preview-hint">Enter a quantity to see its nutrition</p>
        )}
      </div>

      <div className="editor-totals">
        <span>{totals.kcal} kcal</span>
        <span>{totals.protein}g protein</span>
        <span>{totals.carbs}g carbs</span>
        <span>{totals.fat}g fat</span>
      </div>
      <div className="editor-totals-detail">
        <span>{totals.fiber}g fiber</span>
        <span>{totals.calcium}mg calcium</span>
        <span>{totals.iron}mg iron</span>
        <span>{totals.zinc}mg zinc</span>
        <span>{totals.potassium}mg potassium</span>
        <span>{totals.sodium}mg sodium</span>
      </div>

      {savingTemplate ? (
        <div className="template-save-row">
          <input
            type="text"
            className="food-select-input"
            placeholder="Template name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && confirmSaveTemplate()}
            autoFocus
          />
          <button className="btn btn-ghost" onClick={() => setSavingTemplate(false)} type="button">CANCEL</button>
          <button className="btn btn-primary" onClick={confirmSaveTemplate} type="button">SAVE</button>
        </div>
      ) : (
        localItems.length > 0 && (
          <button className="btn btn-ghost full-width" onClick={() => setSavingTemplate(true)} type="button" style={{ marginTop: 12 }}>
            SAVE AS TEMPLATE
          </button>
        )
      )}

      <div className="editor-actions">
        <button className="btn btn-ghost" onClick={onClose} type="button">CANCEL</button>
        {isCustom && (
          <button className="btn btn-ghost" onClick={onReset} type="button">RESET TO PLAN</button>
        )}
        <button className="btn btn-primary" onClick={() => onSave(localItems)} type="button">SAVE</button>
      </div>
    </div>
  )
}
